import { z } from "zod";

/**
 * Permission modes supported by the Claude Agent SDK.
 * Runtime mirror of the SDK's type-only `PermissionMode` union — the SDK
 * exports no runtime enum, and this package must not depend on
 * `@anthropic-ai/claude-agent-sdk` (backend-only). A type-equality assertion
 * in `agent-runner.constant.ts` fails typecheck if this drifts from the SDK.
 * `auto` is TypeScript-SDK-only — model-classified approvals.
 */
export const ZSdkPermissionMode = z.enum([
  "default",
  "acceptEdits",
  "bypassPermissions",
  "plan",
  "dontAsk",
  "auto"
]);

export type SdkPermissionMode = z.infer<typeof ZSdkPermissionMode>;

/**
 * The user-selectable mode set exposed by the Cue / Cue message APIs
 * and the frontend mode selector. `auto` is a Cue product mode ("full auto"),
 * NOT the SDK's own `auto` — the Cue layer resolves it to SDK `bypassPermissions`
 * plus AskUserQuestion disabled and a mode-specific prompt suffix, and never
 * sends the SDK's `auto` to the runner (untested in the sandbox). `plan` and
 * `dontAsk` stay out: `dontAsk` would hard-deny Cue's MCP tools, `plan` is
 * untested. This is product policy enforced at the API boundary — everything
 * below the controllers accepts the full SDK union, so widening later is a
 * DTO-options change only.
 */
export const ZCuePermissionMode = ZSdkPermissionMode.extract([
  "default",
  "auto",
  "bypassPermissions"
]);

export type CuePermissionMode = z.infer<typeof ZCuePermissionMode>;
